import { useAuthenticated } from "../../authn";
import { useOrgCtx } from "./Org/useOrgCtx";
import { OrgCategory } from "../../generated/core-api";

/**
 * Custom hook to centralize permission checks related to organizations
 * Permission hierarchy: Provisioner > Admin > Member
 * - Provisioners are super users with admin rights to all orgs
 * - Admins are also members of their orgs
 * - To view an org, you must be a member (or any authenticated user for public orgs)
 * - To manage org documents, you must be an admin
 */
export function usePermissions() {
  const authCtx = useAuthenticated();
  const orgCtx = useOrgCtx();

  /**
   * Checks if the current user is a provisioner (super user)
   */
  const isProvisioner = (): boolean => {
    return authCtx.roles.includes("AgenticProvisioner");
  };

  /**
   * Checks if the current user is a direct member of the specified organization
   * (does not account for provisioner status)
   */
  const isDirectMemberOfOrg = (orgId: string): boolean => {
    return orgCtx.ownRoleInOrg(orgId) !== undefined;
  };

  /**
   * Checks if the current user is a direct admin of the specified organization
   * (does not account for provisioner status)
   */
  const isDirectAdminOfOrg = (orgId: string): boolean => {
    const role = orgCtx.ownRoleInOrg(orgId);
    return role?.role === "admin";
  };

  /**
   * Checks if the current user is a member of the specified organization
   * (either a direct member or an admin)
   */
  const isMemberOfOrg = (orgId: string): boolean => {
    return isDirectMemberOfOrg(orgId) || isAdminOfOrg(orgId);
  };

  /**
   * Checks if the current user is an admin of the specified organization
   * (either a direct admin or a provisioner)
   */
  const isAdminOfOrg = (orgId: string): boolean => {
    return isDirectAdminOfOrg(orgId) || isProvisioner();
  };

  /**
   * Checks if the current user can view the specified organization.
   * Public orgs are viewable by all authenticated users.
   * Other orgs require membership.
   */
  const canViewOrg = (orgId: string, category: OrgCategory): boolean => {
    if (category === "public") return true;
    return isMemberOfOrg(orgId);
  };

  return {
    isProvisioner,
    isDirectMemberOfOrg,
    isDirectAdminOfOrg,
    isMemberOfOrg,
    isAdminOfOrg,
    canViewOrg,
  };
}
